import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Skeleton,
  Alert,
  alpha,
  Tooltip,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';
import { canModifyRecord } from '../utils/permissions';
import { isImage, isPdf, isVideo, isHtml } from '../utils/documentTypes';
import DocumentMediaDialog from './DocumentMediaDialog';
import DocumentThumbnail from './DocumentThumbnail';

const StyledDocCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  transition: 'all 0.2s ease',
  border: '1px solid',
  borderColor: theme.palette.divider,
  overflow: 'hidden',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    transform: 'translateY(-1px)',
  },
}));

const DocumentsTab = ({ playerId, onDataLoaded }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, doc: null });
  const [editDialog, setEditDialog] = useState({ open: false, doc: null, titre: '' });
  const [mediaPreview, setMediaPreview] = useState({ open: false, index: -1 });
  const [mediaDoc, setMediaDoc] = useState(null); // aperçu vidéo / html
  const [saving, setSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));

  const getAvatarUrl = (userId) => {
    return `${getMediaBaseUrl()}/users/${userId}.png?${new Date().getTime()}`;
  };

  const getDocumentFileUrl = (filename) => {
    return `${getMediaBaseUrl()}/documents/${filename}?${new Date().getTime()}`;
  };

  const fetchDocuments = useCallback(async () => {
    if (!playerId) return;
    setLoading(true);
    try {
      const response = await api.get('/api/documents', { params: { playerId } });
      setDocuments(response.data);
      if (onDataLoaded) onDataLoaded(response.data.length > 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(t('playerCardCreator.documentsTab.fetchError'));
      if (onDataLoaded) onDataLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [playerId, onDataLoaded, t]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const imageDocuments = useMemo(
    () => documents.filter(d => isImage(d.file_type)),
    [documents]
  );

  const currentImage = mediaPreview.index >= 0 ? imageDocuments[mediaPreview.index] : null;

  const handlePrevImage = useCallback(() => {
    setMediaPreview(prev => {
      if (prev.index < 0 || imageDocuments.length === 0) return prev;
      const next = (prev.index - 1 + imageDocuments.length) % imageDocuments.length;
      return { ...prev, index: next };
    });
  }, [imageDocuments.length]);

  const handleNextImage = useCallback(() => {
    setMediaPreview(prev => {
      if (prev.index < 0 || imageDocuments.length === 0) return prev;
      const next = (prev.index + 1) % imageDocuments.length;
      return { ...prev, index: next };
    });
  }, [imageDocuments.length]);

  useEffect(() => {
    if (!mediaPreview.open) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') handlePrevImage();
      else if (e.key === 'ArrowRight') handleNextImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [mediaPreview.open, handlePrevImage, handleNextImage]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Delete
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.doc) return;
    setSaving(true);
    try {
      await api.delete(`/api/documents/${deleteDialog.doc.id}`);
      setDocuments(prev => prev.filter(d => d.id !== deleteDialog.doc.id));
      setDeleteDialog({ open: false, doc: null });
    } catch (err) {
      console.error('Error deleting document:', err);
      alert(t('playerCardCreator.documentsTab.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  // Edit title
  const handleEditSave = async () => {
    if (!editDialog.doc) return;
    setSaving(true);
    try {
      await api.put(`/api/documents/${editDialog.doc.id}`, { titre: editDialog.titre });
      setDocuments(prev => prev.map(d =>
        d.id === editDialog.doc.id ? { ...d, titre: editDialog.titre } : d
      ));
      setEditDialog({ open: false, doc: null, titre: '' });
    } catch (err) {
      console.error('Error updating document:', err);
      alert(t('playerCardCreator.documentsTab.editError'));
    } finally {
      setSaving(false);
    }
  };

  // Open preview
  const handleOpenFile = (doc) => {
    if (isImage(doc.file_type)) {
      const idx = imageDocuments.findIndex(d => d.id === doc.id);
      if (idx >= 0) setMediaPreview({ open: true, index: idx });
    } else if (isVideo(doc.file_type) || isHtml(doc.file_type)) {
      setMediaDoc(doc);
    } else if (isPdf(doc.file_type)) {
      window.open(getDocumentFileUrl(doc.filename), '_blank');
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (documents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
        <InfoOutlinedIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          {t('playerCardCreator.documentsTab.noDocuments')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FolderOpenIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1rem' }}>
          {t('playerCardCreator.documentsTab.title')} ({documents.length})
        </Typography>
      </Box>

      {/* Documents Grid */}
      <Grid container spacing={2}>
        {documents.map((doc) => (
          <Grid item xs={12} sm={6} md={4} key={doc.id}>
            <StyledDocCard>
              {/* Thumbnail / Preview zone */}
              <Box
                onClick={() => handleOpenFile(doc)}
                sx={{
                  height: 160,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  backgroundColor: 'grey.100',
                  position: 'relative'
                }}
              >
                <DocumentThumbnail doc={doc} iconSize={60} />

                {/* Open icon overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '.MuiCard-root:hover &': { opacity: 1 }
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
              </Box>

              {/* Info section */}
              <Box sx={{ p: 1.5 }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.5 }}>
                  {doc.titre || doc.original_name}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(doc.created_at)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    • {formatFileSize(doc.file_size)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Author */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Avatar
                      src={getAvatarUrl(doc.user_id)}
                      alt={doc.user_nom || doc.username}
                      sx={{ width: 20, height: 20 }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {doc.user_prenom ? `${doc.user_prenom} ${doc.user_nom}` : doc.username}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  {canModifyRecord(user, doc.user_id) && (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <Tooltip title={t('playerCardCreator.documentsTab.editTitle')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditDialog({ open: true, doc, titre: doc.titre || '' });
                          }}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('playerCardCreator.documentsTab.delete')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, doc });
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            </StyledDocCard>
          </Grid>
        ))}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, doc: null })}>
        <DialogTitle>{t('playerCardCreator.documentsTab.deleteConfirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('playerCardCreator.documentsTab.deleteMessage', {
              name: deleteDialog.doc?.titre || deleteDialog.doc?.original_name
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, doc: null })} disabled={saving}>
            {t('playerCardCreator.documentsTab.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            {saving ? t('playerCardCreator.documentsTab.deleting') : t('playerCardCreator.documentsTab.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Title Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, doc: null, titre: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{t('playerCardCreator.documentsTab.editTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('playerCardCreator.documentsTab.titleLabel')}
            fullWidth
            value={editDialog.titre}
            onChange={(e) => setEditDialog(prev => ({ ...prev, titre: e.target.value }))}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, doc: null, titre: '' })} disabled={saving}>
            {t('playerCardCreator.documentsTab.cancel')}
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}>
            {saving ? t('playerCardCreator.documentsTab.saving') : t('playerCardCreator.documentsTab.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={mediaPreview.open}
        onClose={() => setMediaPreview({ open: false, index: -1 })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
            {currentImage && (currentImage.titre || currentImage.original_name)}
            {imageDocuments.length > 1 && ` (${mediaPreview.index + 1}/${imageDocuments.length})`}
          </Typography>
          <IconButton onClick={() => setMediaPreview({ open: false, index: -1 })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, position: 'relative' }}>
          {imageDocuments.length > 1 && (
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                zIndex: 1
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          {currentImage && (
            <Box
              component="img"
              src={getDocumentFileUrl(currentImage.filename)}
              alt={currentImage.titre || currentImage.original_name}
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
          {imageDocuments.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                zIndex: 1
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </DialogContent>
      </Dialog>

      {/* Video / HTML Preview Dialog */}
      <DocumentMediaDialog
        open={!!mediaDoc}
        doc={mediaDoc}
        fileUrl={mediaDoc ? getDocumentFileUrl(mediaDoc.filename) : null}
        onClose={() => setMediaDoc(null)}
      />
    </Box>
  );
};

export default DocumentsTab;
